import { useState } from 'react';

import moment from 'moment';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { ActivityBreakdown } from './activity-breakdown';
import { ChronologyStatBlock } from './chronology-stat-block';
import { DatePresetOptions } from './date-preset-options';
import { StatisticBlock } from './statistic-block';
import { useDownloadUsageData } from '@/api/mutations/useDownloadUsageData';
import { useGetOrgActivityBreakdown } from '@/api/queries/analyticsQueries/useGetOrgActivityBreakdown';
import { useGetOrgActivityStats } from '@/api/queries/analyticsQueries/useGetOrgActivityStats';
import formatDate from '@/helpers/formatDate';
import { DateRangePicker } from '@/new/components/date-range-picker';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

// Activity tab — org-wide activity totals and most-active user/matter leaderboards,
// scoped to a selectable date range.
export function ActivityTab({
  organisationId,
  isViewingOtherOrg,
}: {
  organisationId: string;
  isViewingOtherOrg: boolean;
}) {
  // Default range is the past month.
  const [dateRange, setDateRange] = useState<DateRange>({
    from: moment().subtract(1, 'months').toDate(),
    to: new Date(),
  });

  function setPresetRange(amount: moment.DurationInputArg1, unit: moment.DurationInputArg2) {
    setDateRange({
      from: moment().subtract(amount, unit).toDate(),
      to: moment().toDate(),
    });
  }

  const { t } = useTranslation();

  const { data: activityStats } = useGetOrgActivityStats({
    data: { organisationId, startDate: dateRange.from, endDate: dateRange.to },
  });
  const { data: activityBreakdown, isLoading: isActivityBreakdownLoading } = useGetOrgActivityBreakdown({
    data: { organisationId, startDate: dateRange.from, endDate: dateRange.to },
  });
  const { fetchDownload: fetchUsageDownload, isLoading: isUsageDownloading } = useDownloadUsageData();

  return (
    <>
      <div className="my-6 flex justify-between gap-4">
        <div className="ml-2">
          <h2 className="text-lg font-semibold text-gray-900">Activity Overview</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {t('Organisation')}-wide activity totals for {dateRange.from ? formatDate(dateRange.from) : ''} to{' '}
            {dateRange.to ? formatDate(dateRange.to) : ''}
          </p>
        </div>

        <div className="flex grow flex-col items-end justify-end gap-2">
          <DatePresetOptions setPresetRange={setPresetRange} />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={isUsageDownloading}
              onClick={() => fetchUsageDownload({ organisationId, startDate: dateRange.from, endDate: dateRange.to })}
            >
              <Icon className={isUsageDownloading ? 'lucide--loader-circle animate-spin' : 'lucide--download'} />
              Download Usage
            </Button>
            <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[repeat(auto-fit,_minmax(11rem,_auto))] flex-wrap gap-3">
        <StatisticBlock title="Matters" value={activityStats?.matters} />
        <StatisticBlock title="Pages" value={activityStats?.pages} />
        <StatisticBlock title="Kim threads" value={activityStats?.kimThreads} />
        <ChronologyStatBlock
          factChronologies={activityStats?.chronologies}
          docChronologies={activityStats?.docChronologies}
        />
      </div>

      <div className="my-6">
        <ActivityBreakdown
          breakdown={activityBreakdown}
          isLoading={isActivityBreakdownLoading}
          organisationId={organisationId}
          startDate={dateRange.from}
          endDate={dateRange.to}
          isViewingOtherOrg={isViewingOtherOrg}
        />
      </div>
    </>
  );
}
